import { Controller } from "@hotwired/stimulus"
import { MELD_PROVIDER_ICONS_SMALL as _MELD_PROVIDER_ICONS, meldLaunchProvider, meldQuoteValue } from "./meldProviderIcons.js"
import { CHECKOUT_ONRAMP_PAYMENT_IN_PROGRESS } from "./checkoutDepositEmbedController.js"
import { meldOpensProviderDirectly } from "./meldSkipConfirm.mjs"



// Country codes Meld serves that have no Unicode flag: AN was withdrawn from ISO 3166, XK is
// user-assigned. See _countryFlag.
const MELD_COUNTRIES_WITHOUT_FLAG = new Set(["AN", "XK"])

// Quick-amount chips. The ladder is the one shown for dollars and euros; the shape is its rungs
// as multiples of the first, so any other currency can be given the same ladder in its own money.
const MELD_PRESET_LADDER = [50, 100, 300, 500]
const MELD_PRESET_SHAPE  = MELD_PRESET_LADDER.map(v => v / MELD_PRESET_LADDER[0]) // 1 · 2 · 6 · 10
const MELD_MAJOR_CURRENCIES = new Set(["USD", "EUR"])

export default class extends Controller {
    static targets = [
        // Country header selector
        "countryFlag", "countrySearch", "countryDropdown", "countryDropdownPanel",
        "countrySearchWrapper", "countryCode",
        // You pay
        "amountInput", "currencySymbol", "fiatCurrencyLabel", "presetChip", "presetRow",
        "limitsLabel", "rateLabel",
        // Currency selector (the chip next to the amount)
        "currencyWrapper", "currencyDropdown", "currencyDropdownPanel", "currencyCaret",
        // You receive
        "youReceiveAmount", "emptyNotice", "purchaseCheck",
        // Provider row (inside you-receive, below separator)
        "providerRow", "providerName", "providerBestBadge", "providerLogoWrapper", "seeMoreForm", "compareMethodInput", "compareAmountInput", "compareCurrencyInput", "compareProviderInput", "compareCountryInput",
        // Provider picker (bottom sheet)
        "providerDropdown", "providerList",
        // Countdown
        "stalenessBar", "countdownLabel",
        // Footer
        "continueBtn", "confirmForm",
        // Provider iframe overlay
        "providerOverlay", "providerFrame", "providerOverlayTitle",
        // Buy-checkout express: full-card cover while we auto-quote → Confirm & Pay
        "expressLoading",
    ]

    static values = {
        csrf:             String,
        checkoutOrigin:   String,
        embedToken:       String,
        wallet:           String,
        destCc:           String,
        customerId:       String,
        countryCode:      String,
        currency:         String,
        limits:           String,
        paymentMethod:    String,
        selectedProvider: String,
        expressAmount:    Number,
        expressPayAmount: Number,   // onramp-only checkout: the all-in "you pay" every quote opens on (CS-3523)
        minAmount:        Number,
        initialAmount:    Number,
        // Translated copy the controller writes into the DOM. Passing it in as a
        // value keeps the strings in the TOML bundles instead of this file.
        countdownLabel:   String,
        openingLabel:     String,
        purchaseTotal:    Number,   // buy checkout: every quote must deliver at least this (COI-833)
        skipConfirm:      Boolean,  // buy checkout, one provider, all-in total shown: no Confirm step (CS-3534)
    }

    connect() {
        // Capture the server-rendered button label so a reset restores the
        // translated text rather than an English literal.
        this._continueLabel  = this.hasContinueBtnTarget ? this.continueBtnTarget.textContent.trim() : ""
        this._allQuotes      = []
        this._quotes         = []
        this._paymentMethods = []
        this._selectedMethod = this.paymentMethodValue || "CREDIT_DEBIT_CARD" // card by default — widest coverage (COI-365)
        this._countries      = []
        this._fiatCode       = "USD"
        // Resolved per country from Meld's defaults; also the currency we quote in.
        this._currencyByCountry  = new Map()
        // The quotable pay-in currencies of the selected country, in the order the server ranked
        // them (the country's own first). Drives the chip's dropdown.
        this._currencies         = []
        // Set once the shopper picks a currency themselves. An explicit pick outranks the country's
        // default and survives everything except a country change — including the automatic switch
        // to dollars, which exists for currencies nobody can price, not to overrule a choice.
        this._currencyPinned     = false
        // Fiat per destination-token unit, learned from the last quote. Sizes the preset chips
        // for non-USD currencies; 0 until the first quote of a currency lands.
        this._fxRate             = 0
        // Per-currency floors Meld reported on a rejected quote — the only source for currencies
        // its limits feed omits. Survives country switches within the page.
        this._minByCurrency      = new Map()
        // The quick-amount chips are drawn once per currency; this guards against a second draw.
        this._presetsRendered    = false
        this._renderedPresets    = []
        // The server's starting amount applies to the country the page opened on, once.
        this._seedUsed           = false
        this._selectedIdx    = 0
        // serviceProvider the user picked explicitly — provider row, picker sheet, or Compare.
        // Stays null while the selection is ours (best rate), so a re-quote is free to move it.
        this._selectedProvider  = null
        // The currency the amount field was last written for. A country change that keeps the
        // currency leaves the shopper's figure alone; only a change of currency reseeds it.
        this._lastSeededCurrency = ""
        this._sessionStarted    = false
        this._platformFee       = 0
        this._lastQuotedAmount  = 0

        this._countdownTimer    = null
        this._fetchDebounce     = null
        this._minAmount         = 0
        this._maxAmount         = Infinity
        // Buy-checkout only: a hard floor equal to the product price (checkout total). Shoppers
        // can raise the amount but never onramp less than what they're paying for. 0 = standalone
        // wallet, no floor.
        this._productMin        = 0
        this._onProviderClick   = e => {
            const item = e.target.closest("[data-quote-idx]")
            if (!item) return
            const idx = parseInt(item.dataset.quoteIdx)
            if (isNaN(idx)) return
            this._selectedIdx = idx
            this._selectedProvider = (this._quotes[idx] || {}).serviceProvider || null
            this._updateQuoteDisplay()
        }
        this.element.addEventListener("click", this._onProviderClick)
        this._onCloseOverlay = () => {
            this.providerOverlayTarget.removeAttribute("open")
            this.providerFrameTarget.src = ""
        }
        window.addEventListener("meld:close-overlay", this._onCloseOverlay)

        // Buy-checkout express mode (COI-365): the checkout total is supplied up front, so skip
        // the amount + provider steps — auto-quote, auto-pick (low-KYC first), and land straight
        // on Confirm & Pay. Only the buy embed sets expressAmount>0; the standalone wallet keeps
        // expressAmount=0 and behaves exactly as before. Show the cover immediately so the picker
        // never flashes before we advance.
        this._express = (this.hasExpressAmountValue && this.expressAmountValue > 0) || this._expressPayIn() > 0
        if (this._express) this._showExpressLoading(true)

        this._init()

        const input = this.amountInputTarget
        if (input.value) {
            const len = input.value.length
            input.setSelectionRange(len, len)
        }
    }

    disconnect() {
        if (this._countdownTimer) clearInterval(this._countdownTimer)
        if (this._fetchDebounce)  clearTimeout(this._fetchDebounce)
        if (this._onProviderClick) this.element.removeEventListener("click", this._onProviderClick)
        if (this._onCloseOverlay) window.removeEventListener("meld:close-overlay", this._onCloseOverlay)
        if (this._onDocumentClick) document.removeEventListener("click", this._onDocumentClick)
        this._closeCurrencyDropdown()
    }

    // ── API ──────────────────────────────────────────────────

    async _apiFetch(method, path, body) {
        const opts = { method, headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfValue } }
        // When embedded cross-site, Chrome withholds the session cookie from this XHR, so carry
        // the short-lived session token in X-Session instead (server accepts either). Empty in
        // standalone, where the cookie works and stays HttpOnly.
        if (this.embedTokenValue) opts.headers["X-Session"] = this.embedTokenValue
        if (body) opts.body = JSON.stringify(body)
        const r    = await fetch(path, opts)
        const data = await r.json()
        if (!r.ok) {
            // Carry the parsed body: Meld's rejections name the real limit (INVALID_AMOUNT_TOO_LOW
            // says what the minimum is), which is the only place some currencies report it, and
            // our own structured errors (amount_below_purchase) carry their figures the same way.
            const err   = new Error(data.error || data.message || ("HTTP " + r.status))
            err.status  = r.status
            err.payload = data
            throw err
        }
        return data
    }

    // ── Init ─────────────────────────────────────────────────

    async _init() {
        try {
            const data = await this._apiFetch("GET", "/meld/api/countries")
            this._countries = Array.isArray(data) ? data : (data.countries || [])
            if (!this._countries.length) { this._showEmpty(); return }
            this._buildCountryDropdown()
            const preselected = this.countryCodeValue
            const initial = preselected
                ? this._countries.find(c => c.countryCode === preselected) || this._countries[0]
                : this._countries[0]
            // The currency the page was rendered with is the shopper's own pick coming back from
            // Compare or Confirm — the server only sets it after confirming a partner still serves
            // it. Anything else opens on the country's default.
            if (initial) await this._selectCountry(initial, this.currencyValue)
        } catch (e) {
            // In express mode, drop the loading cover and fall back to the picker instead of
            // leaving the shopper on a spinner.
            if (this._express) { this._exitExpress(); return }
            this._showEmpty()
            console.error("meld init failed", e)
        }
    }

    // ── Country ───────────────────────────────────────────────

    _buildCountryDropdown() {
        this.countrySearchTarget.addEventListener("input", () => {
            const q = this.countrySearchTarget.value.toLowerCase()
            this._renderCountryItems(this._countries.filter(c =>
                (c.name || c.countryName || "").toLowerCase().includes(q) ||
                c.countryCode.toLowerCase().startsWith(q)
            ))
        })
        this._onDocumentClick = e => {
            if (!this.countrySearchWrapperTarget.contains(e.target)) {
                this.countryDropdownPanelTarget.style.display = "none"
            }
            // The panel is fixed, so it is no longer inside the wrapper as far as the DOM is
            // concerned — a click on one of its rows must not count as a click outside.
            const inCurrency = (this.hasCurrencyWrapperTarget && this.currencyWrapperTarget.contains(e.target)) ||
                (this.hasCurrencyDropdownPanelTarget && this.currencyDropdownPanelTarget.contains(e.target))
            if (!inCurrency) this._closeCurrencyDropdown()
        }
        document.addEventListener("click", this._onDocumentClick)
    }

    toggleCountryDropdown() {
        const panel  = this.countryDropdownPanelTarget
        const isOpen = panel.style.display !== "none"
        if (isOpen) {
            panel.style.display = "none"
        } else {
            panel.style.display = ""
            this._renderCountryItems(this._countries)
            this.countrySearchTarget.value = ""
            setTimeout(() => this.countrySearchTarget.focus(), 0)
        }
    }

    _renderCountryItems(list) {
        const dropdown = this.countryDropdownTarget
        dropdown.replaceChildren()
        list.slice(0, 60).forEach(c => {
            const div = document.createElement("div")
            div.className   = "meld-country-item"
            div.textContent = this._countryFlag(c.countryCode) + "  " + (c.name || c.countryName) + " (" + c.countryCode + ")"
            div.addEventListener("mousedown", e => {
                e.preventDefault()
                this.countryDropdownPanelTarget.style.display = "none"
                // Which provider pays out most is a fact about the route, and the route just
                // changed: a new country brings its own providers, fees and ranking. Keeping the
                // old pick would go on recommending a provider that was best somewhere else, and
                // could pin one this country is not even served by. Editing the amount clears the
                // pick for the same reason; this is the other half of it.
                this._selectedProvider     = null
                this.selectedProviderValue = ""
                this._selectCountry(c)
            })
            dropdown.appendChild(div)
        })
    }

    async _selectCountry(c, preferredCurrency = "") {
        this.countryCodeTarget.value       = c.countryCode
        this.countryFlagTarget.textContent = this._countryFlag(c.countryCode)
        // A currency pick belongs to the country it was made in: the new country may not even be
        // quotable in it, so the choice goes with the old one.
        this._currencyPinned = false

        await this._loadFiatForCountry(c.countryCode, preferredCurrency)
    }

    // Flag emoji are built from the country code's regional indicators, which only render for
    // codes Unicode actually assigns a flag to. Meld's list carries two that it doesn't: AN, the
    // Netherlands Antilles, withdrawn from ISO 3166 in 2010, and XK for Kosovo, which is
    // user-assigned rather than official. Both would come out as two blank boxes, so they get the
    // globe instead.
    _countryFlag(cc) {
        const code = (cc || "").toUpperCase()
        if (code.length !== 2 || MELD_COUNTRIES_WITHOUT_FLAG.has(code)) return "🌍"
        return String.fromCodePoint(...[...code].map(ch => ch.charCodeAt(0) + 127397))
    }

    // ── Fiat ─────────────────────────────────────────────────

    async _loadFiatForCountry(cc, preferredCurrency = "") {
        // Quote in the country's own currency, so what the shopper types is what the provider
        // charges: 50 RSD prices as 50 dinars, not as 50 dollars. COI-310 had pinned this to USD
        // for provider coverage; that concern is now handled by the USD retry in fetchQuotes,
        // which only kicks in for currencies Meld genuinely cannot price.
        const entry      = await this._defaultCurrencyForCountry(cc)
        this._currencies = entry.currencies

        // A currency handed to us counts as a pick only when it differs from what this country
        // would have opened on anyway. Confirm's back arrow carries the currency it priced in
        // whether or not the shopper chose it, and treating the default as a choice would switch
        // off the dollar fallback for someone who never asked for anything.
        const wanted = (preferredCurrency || "").toUpperCase()
        if (wanted && this._currencies.includes(wanted)) {
            this._fiatCode       = wanted
            this._currencyPinned = wanted !== entry.code
        } else {
            this._fiatCode = entry.code
        }
        this._renderCurrencyChip()
        // The old country's rate and chips say nothing about the new one's.
        this._fxRate = 0
        this._presetsRendered = false
        this._renderedPresets = []
        this._applyCurrencyLabels(this._fiatCode)
        this._applyLimitsForCurrency()

        if (this._express) {
            await this._expressQuoteAndAdvance()
        } else {
            await this.fetchQuotes()
        }
    }

    // Currency chip + symbol next to the amount, for whatever currency we are quoting in.
    _applyCurrencyLabels(code) {
        const symbol = this._getCurrencySymbol(code)
        this.fiatCurrencyLabelTarget.textContent = code
        this.currencySymbolTarget.textContent    = symbol
        // Glyphs sit tight against the figure ("$100"), letters can't — "KM100" and "RSD5.000"
        // read as one word. Bosnia's mark and every currency whose symbol is just its code get a
        // space; the dollar and the euro are unchanged.
        this.currencySymbolTarget.style.marginRight = /[A-Za-z]$/.test(symbol) ? "0.22em" : ""
    }

    // The country's pay-in currencies and the bounds of the route serving each, in one call — the
    // server resolves them from the same partner lookup, so switching currency afterwards costs no
    // round trip. Every currency listed is one a partner actually quotes; the list is ranked with
    // the country's own money first. Cached per country for the life of the controller, and for a
    // week on the server. Falls back to USD so a failed lookup never blanks the field.
    async _defaultCurrencyForCountry(cc) {
        const key = (cc || "").toUpperCase()
        if (!key) return { code: "USD", limits: "[]", currencies: ["USD"] }
        if (this._currencyByCountry.has(key)) {
            const cached = this._currencyByCountry.get(key)
            this.limitsValue = cached.limits
            return cached
        }

        let code       = "USD"
        let limits     = "[]"
        let currencies = []
        try {
            const data  = await this._apiFetch("GET", `/meld/api/defaults?countries=${encodeURIComponent(key)}`)
            const list  = Array.isArray(data) ? data : (data.items || [])
            const entry = list.find(e => (e.countryCode || "").toUpperCase() === key) || list[0]
            if (entry && entry.defaultCurrencyCode) code = entry.defaultCurrencyCode.toUpperCase()
            if (entry && Array.isArray(entry.limits)) limits = JSON.stringify(entry.limits)
            if (entry && Array.isArray(entry.supportedCurrencies)) {
                currencies = entry.supportedCurrencies.map(x => String(x).toUpperCase())
            }
        } catch (e) {
            console.error("meld: failed to resolve currency for", key, e)
        }
        // The currency we quote in always has to be offered, even when the lookup failed and left
        // the list empty — otherwise the chip would show a currency its own dropdown denies.
        if (!currencies.includes(code)) currencies = [code, ...currencies]

        const resolved = { code, limits, currencies }
        this._currencyByCountry.set(key, resolved)
        this.limitsValue = limits
        return resolved
    }

    // ── Currency ─────────────────────────────────────────────

    // The chip only opens a dropdown when there is something to choose between; with a single
    // quotable currency it stays the plain label it has always been.
    _renderCurrencyChip() {
        if (!this.hasCurrencyCaretTarget) return
        const selectable = this._currencies.length > 1
        this.currencyCaretTarget.style.display = selectable ? "" : "none"
        if (!selectable) this._closeCurrencyDropdown()
    }

    toggleCurrencyDropdown(event) {
        if (this._currencies.length < 2) return
        const panel = this.currencyDropdownPanelTarget
        if (panel.style.display !== "none") {
            this._closeCurrencyDropdown()
            return
        }
        this._renderCurrencyItems()
        panel.style.display  = ""
        this._currencyAnchor = event ? event.currentTarget : null
        this._positionCurrencyPanel()

        // The panel is fixed to the viewport so the card can't clip it, which also means it no
        // longer moves with the chip on its own. Anything that shifts the chip has to move it too.
        this._onCurrencyReflow = () => this._positionCurrencyPanel()
        window.addEventListener("scroll", this._onCurrencyReflow, true)
        window.addEventListener("resize", this._onCurrencyReflow)
    }

    _closeCurrencyDropdown() {
        if (this.hasCurrencyDropdownPanelTarget) this.currencyDropdownPanelTarget.style.display = "none"
        if (this._onCurrencyReflow) {
            window.removeEventListener("scroll", this._onCurrencyReflow, true)
            window.removeEventListener("resize", this._onCurrencyReflow)
            this._onCurrencyReflow = null
        }
    }

    // Hangs the panel under the chip, right edges aligned, and never closer than a margin to either
    // side of the screen — the list is wider than the chip, so on a narrow viewport aligning alone
    // would push it off the edge.
    _positionCurrencyPanel() {
        const anchor = this._currencyAnchor
        if (!anchor) return
        const margin = 8
        const rect   = anchor.getBoundingClientRect()
        const panel  = this.currencyDropdownPanelTarget
        panel.style.minWidth = `${Math.round(rect.width)}px`
        const left = Math.min(
            Math.max(margin, rect.right - panel.offsetWidth),
            Math.max(margin, window.innerWidth - panel.offsetWidth - margin),
        )
        panel.style.top  = `${Math.round(rect.bottom + margin)}px`
        panel.style.left = `${Math.round(left)}px`
    }

    _renderCurrencyItems() {
        const dropdown = this.currencyDropdownTarget
        dropdown.replaceChildren()
        this._currencies.forEach(code => {
            const div = document.createElement("div")
            div.className   = "meld-country-item"
            div.textContent = code === this._fiatCode ? `${code}  ✓` : code
            div.addEventListener("mousedown", e => {
                e.preventDefault()
                this._closeCurrencyDropdown()
                this._selectCurrency(code)
            })
            dropdown.appendChild(div)
        })
    }

    // Switching currency re-prices everything: the rate, the chips and the limits all belonged to
    // the old money. The amount is left to _applyLimitsForCurrency, which seeds a sensible figure
    // for the new one rather than carrying over a number that means something else.
    async _selectCurrency(code) {
        const next = (code || "").toUpperCase()
        if (!next || next === this._fiatCode) return

        this._fiatCode        = next
        this._currencyPinned  = true
        this._fxRate          = 0
        this._presetsRendered = false
        this._renderedPresets = []
        this._hideEmptyNotice()
        this._applyCurrencyLabels(next)
        this._applyLimitsForCurrency()
        await this.fetchQuotes()
    }


    _applyLimitsForCurrency() {
        try {
            const limits = JSON.parse(this.limitsValue || "[]")
            const entry  = limits.find(l => l.currencyCode === this._fiatCode)
            if (entry) {
                // Whole units in both directions: a floor of 4.60 becomes 5 and a ceiling of
                // 20,000.99 becomes 20,000, so neither the Min/Max line nor the amount the field
                // snaps to ever carries a stray remainder. Rounding the floor up and the ceiling
                // down keeps both inside the range the partner actually accepts.
                this._minAmount = entry.minimumAmount ? Math.ceil(entry.minimumAmount) : 0
                this._maxAmount = entry.maximumAmount ? Math.floor(entry.maximumAmount) : Infinity
            } else {
                this._minAmount = 0
                this._maxAmount = Infinity
            }
            // The bounds are shown under the amount (Min · Max) and enforced by clamping the
            // field to them — never by an error message. The quote stays the final gate.
        } catch { /* keep existing limits */ }

        // Minimum Meld itself reported for this currency on an earlier rejection. Meld's limits
        // feed doesn't carry every currency, so for those this is the only real floor we have —
        // and it outranks the feed when both exist, because it comes from the quote engine.
        const learnedMin = this._minByCurrency.get(this._fiatCode) || 0
        if (learnedMin > 0) {
            this._minAmount = Math.max(this._minAmount || 0, Math.ceil(learnedMin))
        }

        // Buy-checkout floor: the product price is a hard minimum. Raise the effective minimum so
        // presets below it are dropped and the default can't start below what the shopper owes.
        this._productMin = this.hasMinAmountValue && this.minAmountValue > 0 ? this.minAmountValue : 0
        if (this._productMin > 0) {
            this._minAmount = Math.max(this._minAmount || 0, this._productMin)
        }

        // Chips are drawn once per currency and not re-labelled for cosmetic reasons: a redraw is
        // the flicker where "8 AED" turns into "100" under the user's eyes. Bounds are not
        // cosmetic though — when a minimum arrives late (Meld reports it only on a rejected
        // quote) chips that now sit outside the range are redrawn, because a chip below the
        // floor is a button that can only fail.
        if ((!this._presetsRendered && this._presetsAreFinal()) || this._renderedPresetsOutOfBounds()) {
            this._renderPresetChips(this._generatePresets())
        }
        this._renderLimitsLabel()

        // The field opens on an amount a person would plausibly deposit — the first quick-amount,
        // 50 where the minimum is 9 — rather than on a bound. A minimum is a constraint, not a
        // suggestion, and a maximum even less so.
        //
        // An amount handed over by the server outranks it, but only the first time: that is the
        // buy-checkout total or the figure carried back from Compare, and it is meant for the
        // country the page opened on. Every later country brings its own currency, where the old
        // number means nothing.
        // The handed-over amount is only honoured when it is usable as-is. Clamping it instead
        // would land the field exactly on a bound — a $20 default against a 106 AED floor becomes
        // "AED 106", which is the one thing the field must never open on.
        const seed = !this._seedUsed && this.hasInitialAmountValue ? this.initialAmountValue : 0
        this._seedUsed = true

        const withinBounds = seed > 0 &&
            seed >= (this._minAmount || 0) &&
            seed <= (this._maxAmount || Infinity)

        // A figure is only replaced when the currency it was written in changes. Reseeding on
        // every country was wrong: pick a different country that quotes in the same currency —
        // and USD is now the default in all of them — and the amount the shopper had entered was
        // thrown away for a round default. In the buy checkout that meant a $19.70 purchase
        // reopening as 50.
        //
        // The current value is still put through the new bounds, because the new country's
        // provider limits may not admit it.
        const sameCurrency = this._lastSeededCurrency === this._fiatCode
        const onScreen     = this._parseAmount(this.amountInputTarget.value)
        this._lastSeededCurrency = this._fiatCode

        if (sameCurrency && onScreen > 0) {
            this.amountInputTarget.value = this._formatAmount(this._clampToLimits(onScreen))
            this.presetChipTargets.forEach(chip => chip.classList.remove("meld-preset-chip-active"))
            return
        }

        const start = withinBounds ? seed : this._defaultAmount()
        // Not rounded: an amount with cents (23.44) must survive a reload as typed. _formatAmount
        // already caps at the currency's own precision.
        this.amountInputTarget.value = this._formatAmount(this._clampToLimits(start))
        this.presetChipTargets.forEach(chip => chip.classList.remove("meld-preset-chip-active"))
    }

    // Quick-amounts in the currency being quoted. USD keeps the familiar 20/50/100/200 ladder;
    // every other currency scales that ladder by the rate the last quote implied and rounds to a
    // figure a person would actually pick — so Serbia gets 2.000/5.000/10.000/20.000 RSD instead
    // of four amounts that all sit below the provider minimum. Tiers outside the limits drop out.
    // Every chip is an amount the currency will actually quote: never under its minimum, never
    // over its maximum. A chip outside those bounds is a button that can only fail.
    //
    // Dollars and euros are the exception, by design: their ladder is fixed at 50 · 100 · 300 ·
    // 500 and is never rebuilt, so the two currencies people move between most read the same
    // every time.
    _generatePresets() {
        const min = this._minAmount || 0
        const max = this._maxAmount || Infinity

        // Contradictory bounds (a ceiling below the floor) leave no amount that satisfies both,
        // so there is no honest chip to offer — the row stays empty rather than showing a button
        // that can only fail.
        if (max < min) return []

        const inRange = list => list
            .filter(v => v >= min && v <= max)
            .filter((v, i, all) => all.indexOf(v) === i) // rounding can collapse two tiers into one

        let kept = inRange(this._presetTiers())

        // The ladder is built for the currency at large, so on a route with tight bounds most of
        // it can fall outside them. Rebuild it from a round figure above the minimum.
        if (kept.length < 2 && min > 0) {
            kept = inRange(MELD_PRESET_SHAPE.map(m => this._baseAboveMin() * m))
        }

        if (kept.length) return kept

        // Nothing fits: offer a single amount from inside the bounds. Deliberately not the
        // minimum — a chip sitting exactly on the floor reads as the only allowed amount rather
        // than as a suggestion.
        const ceiling = max === Infinity ? min * 4 : max
        const inside  = Math.min(Math.max(this._niceRound((min + ceiling) / 2), min), max)
        return inside > 0 ? [inside] : []
    }

    // Dollars and euros get the fixed ladder: 50 · 100 · 300 · 500. Any other currency gets that
    // same worth of itself — 5.000 · 10.000 · 30.000 · 50.000 dinars — in figures a person would
    // pick.
    //
    // One base is rounded and then multiplied, rather than rounding each rung on its own: round
    // each and the ladder buckles, because neighbouring amounts snap to the same figure. Large
    // numbers therefore appear only where the rate truly puts them — dinars run in thousands,
    // dong in millions, and neither is padding.
    _presetTiers() {
        // The default ladder stands as long as the route's floor is below its first rung: a $9
        // minimum against a $50 chip changes nothing, and redrawing it would only move buttons
        // under the shopper. Once the floor is above that rung the whole ladder is unusable —
        // every chip a button that can only fail — so it is rebuilt from the minimum instead.
        // This applies to USD and EUR too. It did not have to while USD meant the US; now that
        // USD is the default in every country, its floor varies by route like any other.
        if (MELD_MAJOR_CURRENCIES.has(this._fiatCode) && (this._minAmount || 0) <= MELD_PRESET_LADDER[0]) {
            return MELD_PRESET_LADDER
        }

        // With a rate the ladder is that ladder's worth in this currency; without one (no quote
        // yet) it is built up from the minimum instead.
        const base = this._fxRate > 0
            ? this._niceRound(MELD_PRESET_LADDER[0] * this._fxRate)
            : (this._minAmount > 0 ? this._baseAboveMin() : 0)

        return base > 0 ? MELD_PRESET_SHAPE.map(m => base * m) : MELD_PRESET_LADDER
    }

    // What the amount field opens on. The first quick-amount that the bounds allow — never a
    // bound itself, in any currency. Dollars and euros keep a fixed ladder, so their chips can
    // sit under a route's floor; the field still opens above it, on a round figure of its own.
    _defaultAmount() {
        const min = this._minAmount || 0
        const max = this._maxAmount || Infinity

        const presets = this._renderedPresets.length ? this._renderedPresets : this._generatePresets()
        const usable  = presets.find(v => v >= min && v <= max)
        if (usable) return usable

        const aboveMin = min > 0 ? this._baseAboveMin() : 0
        if (aboveMin > 0 && aboveMin <= max) return aboveMin

        // Bounds this tight leave the minimum as the only quotable amount.
        return min
    }

    // A round figure comfortably above the minimum. The first chip is never the minimum itself:
    // an amount sitting exactly on the floor reads as a constraint rather than as a suggestion.
    _baseAboveMin() {
        let base = this._niceRound(this._minAmount * 2)
        while (base <= this._minAmount) base *= 2
        return base
    }

    // Snaps to the *nearest* 1 / 2 / 2.5 / 5 / 10 × power of ten, so a converted amount reads like
    // a chosen one (4 812 → 5 000). Nearest rather than up, or the ladder buckles: 50 USD of
    // dinars is 5 850, and rounding that up lands on 10 000 — the same figure 100 USD deserves.
    _niceRound(v) {
        if (!(v > 0)) return 0
        const mag = Math.pow(10, Math.floor(Math.log10(v)))
        return [1, 2, 2.5, 5, 10]
            .map(s => s * mag)
            .reduce((best, candidate) => Math.abs(candidate - v) < Math.abs(best - v) ? candidate : best)
    }

    // Rate implied by a quote: fiat per unit of the destination stablecoin, i.e. within rounding
    // of fiat per USD. Only ever used to size the preset chips — nothing is priced with it.
    _updateFxRate(q) {
        if (!q) return
        const src  = parseFloat(q.sourceAmount)      || 0
        const dest = parseFloat(q.destinationAmount) || 0
        if (src <= 0 || dest <= 0) return
        this._fxRate = src / dest
        // Only draws the chips that were still waiting on a rate. Chips already on screen are
        // left exactly as they are — this is the redraw the user sees as a flicker.
        if (!this._presetsRendered) {
            this._renderPresetChips(this._generatePresets())
        }
    }

    // Enough to build a ladder that won't have to change: dollars use the fixed one, another
    // currency needs either its own minimum or a rate from a quote.
    _presetsAreFinal() {
        return this._fiatCode === "USD" || this._fxRate > 0 || this._minAmount > 0
    }

    // True when chips already on screen no longer satisfy the current bounds — the case where a
    // minimum or maximum landed after they were drawn. Dollars and euros keep their fixed ladder,
    // so nothing ever redraws them.
    _renderedPresetsOutOfBounds() {
        if (!this._presetsRendered || !this._renderedPresets.length) return false
        const min = this._minAmount || 0
        const max = this._maxAmount || Infinity
        return this._renderedPresets.some(v => v < min || v > max)
    }

    _renderPresetChips(presets) {
        this._presetsRendered = true
        this._renderedPresets = presets
        // An empty ladder means no amount satisfies the route's bounds; showing an empty row
        // would just be a gap in the layout.
        if (this.hasPresetRowTarget) {
            this.presetRowTarget.style.visibility = presets.length ? "visible" : "hidden"
        }
        this.presetChipTargets.forEach((chip, i) => {
            const v = presets[i]
            if (v == null) {
                chip.style.display = "none"
                chip.dataset.amount = ""
                chip.classList.remove("meld-preset-chip-active")
                return
            }
            chip.style.display = ""
            // The chip carries the exact amount; only its label is shortened.
            chip.dataset.amount = String(v)
            // Plain number, no currency prefix — the currency is already on the amount field and
            // the Min/Max line — and shortened, so a dong chip is "5M" rather than "5,000,000".
            chip.textContent = this._compactAmount(v)
        })
    }

    // Chip labels only: thousands as K, millions as M, one decimal at most. The amount field and
    // every figure that gets quoted keep the full number — this is shorthand for a button, not a
    // rounding of the value.
    _compactAmount(v) {
        if (v >= 1e6) return this._trimTrailingZero(v / 1e6) + "M"
        if (v >= 1e3) return this._trimTrailingZero(v / 1e3) + "K"
        return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(v)
    }

    _trimTrailingZero(n) {
        return String(Math.round(n * 10) / 10).replace(/\.0$/, "")
    }


    // _groupInt groups thousands with a comma, always, whatever locale the browser is in.
    //
    // Intl.NumberFormat(undefined, …) cannot be used for anything this field will read back:
    // in tr-TR and sr-RS it groups with a dot, and _parseAmount treats a dot as the decimal
    // mark. A shopper in Turkey typing 20111 got "20.111" back, the next keystroke re-read that
    // dot as decimals, and the value collapsed to 20.11 — the field looked frozen until they
    // deleted it. This field is comma-grouped and dot-decimal in every locale, and the parser
    // agrees with it by construction.
    _groupInt(digits) {
        return String(digits).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    }

    _parseAmount(str) {
        // Strip all formatting characters except the decimal separator
        return Number.parseFloat(String(str).replace(/[^0-9.]/g, "")) || 0
    }

    _formatAmount(n) {
        if (!n) return ""
        // An amount with a fraction shows every digit the currency has — 32.40 must not collapse
        // to "32.4" when the controller rewrites the field. Whole amounts stay clean ("50", not
        // "50.00"), and a currency without subunits (RSD, JPY) never grows a decimal part.
        const dec      = this._currencyDecimals(this._fiatCode)
        const factor   = Math.pow(10, dec)
        const rounded  = Math.round(n * factor) / factor
        const hasCents = dec > 0 && Math.round(n * factor) % factor !== 0
        const whole    = Math.trunc(rounded)
        const grouped  = this._groupInt(whole)
        if (!hasCents) return grouped
        const cents = Math.round(Math.abs(rounded - whole) * factor)
        return `${grouped}.${String(cents).padStart(dec, "0")}`
    }

    onAmountBlur() {
        if (this._fetchDebounce) clearTimeout(this._fetchDebounce)
        // Out-of-range amounts are corrected, not reported: the field snaps to the bound it broke
        // and quotes for that. The Min/Max line above already says what the bounds are, so an
        // error message underneath would only repeat it back after the fact.
        const input = this.amountInputTarget
        const typed = this._parseAmount(input.value)
        if (!typed) return

        const raw = this._clampToLimits(typed)
        input.value = this._formatAmount(raw)
        // Re-fetch only when the amount actually changed from the last quoted one — blur fires
        // before the click event, so fetching unconditionally would disable the Continue button
        // right as the user clicks it.
        if (raw !== this._lastQuotedAmount) {
            this.fetchQuotes()
        }
    }

    // narrowSymbol keeps it to "$" rather than "US$" outside en-US; older runtimes reject the
    // option, so fall back to the wide symbol and finally to the code itself (e.g. "RSD").
    _getCurrencySymbol(code) {
        for (const currencyDisplay of ["narrowSymbol", "symbol"]) {
            try {
                const sym = new Intl.NumberFormat(undefined, { style: "currency", currency: code, currencyDisplay })
                    .formatToParts(0)
                    .find(p => p.type === "currency")?.value
                if (sym) return sym
            } catch { /* try the next form */ }
        }
        return code
    }

    // Fraction digits the currency actually uses (0 for RSD/JPY, 2 for USD/EUR), from Intl so
    // there is no table to maintain. Used for both formatting and what the input accepts.
    _currencyDecimals(code) {
        try {
            return new Intl.NumberFormat(undefined, { style: "currency", currency: code })
                .resolvedOptions().maximumFractionDigits ?? 2
        } catch { return 2 }
    }

    // "Min €5 · Max €20,000" — the bounds the currency actually allows, under the amount field.
    // Each half appears only once its number is known: with no ceiling reported the line is just
    // the minimum, which is the honest reading of "no maximum on record".
    _renderLimitsLabel() {
        if (!this.hasLimitsLabelTarget) return
        const parts = []
        if (this._minAmount > 0) parts.push(`Min ${this._money(this._minAmount)}`)
        if (this._maxAmount > 0 && this._maxAmount !== Infinity) parts.push(`Max ${this._money(this._maxAmount)}`)
        this.limitsLabelTarget.textContent = parts.join(" · ")
    }

    // "1 EUR = 1.15 USDC" — the conversion rate, with the fees taken out of the pay-in first.
    //
    // Dividing the amount received by the amount paid gives the net result, not a rate: a dollar
    // buying 0.9721 USDC says the dollar lost 3%, when what happened is that fees did. Fees are
    // disclosed on their own line at Confirm; this line is about the conversion.
    _renderRateLabel(q) {
        if (!this.hasRateLabelTarget) return

        const src      = this._sourceAmountWithoutFees(q)
        const dest     = parseFloat(q && q.destinationAmount) || 0
        const destCode = String((q && q.destinationCurrencyCode) || this.destCcValue || "").split("_")[0]
        if (src <= 0 || dest <= 0 || !destCode) {
            this.rateLabelTarget.textContent = ""
            return
        }

        // One unit of a weak currency buys a sliver of a dollar — "1 RSD = 0.0085 USDC" is a
        // number nobody can hold on to. Flip the direction there, so dinars and lira read the way
        // people actually quote them: how much of the currency one dollar costs.
        const rate = dest / src
        if (rate < 0.1) {
            this.rateLabelTarget.textContent = `1 ${destCode} = ${this._formatRate(src / dest)} ${this._fiatCode}`
            return
        }
        this.rateLabelTarget.textContent = `1 ${this._fiatCode} = ${this._formatRate(rate)} ${destCode}`
    }

    // What the pay-in is worth once fees are set aside. Meld reports it directly on most quotes;
    // where it doesn't, the fee total is subtracted. Falls back to the gross amount so the line
    // still shows something rather than disappearing.
    _sourceAmountWithoutFees(q) {
        if (!q) return 0
        const net = parseFloat(q.sourceAmountWithoutFees) || parseFloat(q.fiatAmountWithoutFees) || 0
        if (net > 0) return net

        const gross = parseFloat(q.sourceAmount) || 0
        const fees  = parseFloat(q.totalFee) || 0
        return fees > 0 && fees < gross ? gross - fees : gross
    }

    // Precision follows magnitude: 117 needs no decimals, 1.15 needs two, 0.0085 needs four.
    _formatRate(v) {
        const digits = v >= 100 ? 0 : v >= 1 ? 2 : 4
        return new Intl.NumberFormat(undefined, { maximumFractionDigits: digits }).format(v)
    }

    // _money formats an amount with its currency symbol. Alphabetic codes (RSD, TRY as
    // "RSD") read better with a space — "RSD 516"; glyph symbols ($, ₺, €) don't — "$20".
    _money(n) {
        const sym = this._getCurrencySymbol(this._fiatCode)
        const num = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n)
        return /[A-Za-z]$/.test(sym) ? `${sym} ${num}` : `${sym}${num}`
    }

    // ── Amount input ─────────────────────────────────────────

    onAmountInput() {
        const input  = this.amountInputTarget
        const cursor = input.selectionStart

        // Strip anything that's not a digit, dot, or comma; no leading separator
        const original = input.value
        let clean = original.replace(/[^0-9.,]/g, "").replace(/^[.,]+/, "")
        if (original !== clean) {
            const removed = original.length - clean.length
            input.value = clean
            const pos = Math.max(0, cursor - removed)
            input.setSelectionRange(pos, pos)
        }

        const raw = this._parseAmount(clean)

        // Group the integer part, but leave the fraction exactly as typed. Formatting the whole
        // value through _formatAmount here padded it back to two decimals, which silently undid
        // every backspace in the cents: deleting the last digit of "50.30" produced "50.3" and
        // this immediately restored the "0", so the field looked frozen. Padding belongs on the
        // way in from the server, not under the user's cursor.
        if (clean) {
            // Commas are grouping in this field; "." is the decimal mark, same as _parseAmount reads it.
            const bare = clean.replace(/,/g, "")
            const dot  = bare.indexOf(".")
            const whole = dot === -1 ? bare : bare.slice(0, dot)
            // Fraction digits follow the currency: two for USD/EUR, none for RSD/JPY — where a
            // typed "." is dropped rather than left dangling on a currency that has no subunit.
            const dec   = this._currencyDecimals(this._fiatCode)
            const cents = (dot === -1 || dec === 0) ? null : bare.slice(dot + 1).replace(/\D/g, "").slice(0, dec)

            const grouped = whole ? this._groupInt(Number.parseInt(whole, 10) || 0) : ""
            const formatted = cents === null ? grouped : `${grouped}.${cents}`

            if (input.value !== formatted) {
                const lengthDiff = formatted.length - input.value.length
                input.value = formatted
                const newCursor = Math.max(0, cursor + lengthDiff)
                input.setSelectionRange(newCursor, newCursor)
            }
        }

        if (this._fetchDebounce) clearTimeout(this._fetchDebounce)
        if (raw > 0) {
            this._fetchDebounce = setTimeout(() => this._validateAndFetch(), 700)
        }
    }

    _validateAndFetch() {
        const raw = this._parseAmount(this.amountInputTarget.value)
        if (!raw) return
        // Deliberately does not rewrite the field: onAmountInput already keeps it grouped, and
        // re-formatting here would pad the cents back 700ms after the user deleted them — the
        // same fight, just delayed. fetchQuotes clamps to the limits and corrects the field only
        // when the typed amount is actually out of range.
        this.fetchQuotes()
    }

    onPresetClick(e) {
        const raw = Number.parseFloat(e.currentTarget.dataset.amount) || 0
        this.amountInputTarget.value = this._formatAmount(raw)
        this.presetChipTargets.forEach(chip => {
            chip.classList.toggle("meld-preset-chip-active", chip === e.currentTarget)
        })
        if (this._fetchDebounce) clearTimeout(this._fetchDebounce)
        this.fetchQuotes()
    }

    // ── Quotes ────────────────────────────────────────────────

    async fetchQuotes(isRetry = false) {
        const cc  = this.countryCodeTarget.value
        if (!cc) return
        // Never send an amount the currency's limits already reject: clamp first and show the
        // corrected figure, so a country switch lands on a quotable amount instead of an error.
        const typed = this._parseAmount(this.amountInputTarget.value)
        const amt   = this._clampToLimits(typed)
        if (amt <= 0) return
        if (amt !== typed) {
            this.amountInputTarget.value = this._formatAmount(amt)
        }

        // A new amount re-ranks the providers, so whoever was selected for the old amount — our
        // auto-pick, a tap in the picker, or a return from Compare — no longer means "best value".
        // Drop the pick so _applyMethodFilter takes the top quote for this amount. The ~30s
        // countdown refresh re-quotes the same amount and therefore keeps the pick.
        if (this._lastQuotedAmount && amt !== this._lastQuotedAmount) this._selectedProvider = null

        this._lastQuotedAmount = amt
        if (this._countdownTimer) { clearInterval(this._countdownTimer); this._countdownTimer = null }
        this._setLoading()

        try {
            let data   = await this._requestQuotes(this._fiatCode, amt, cc)
            let quotes = data.quotes || []

            // Meld prices most local currencies, but not all of them. An OK response with an
            // empty list means the currency/country pair has no providers at all — an amount that
            // is merely out of range comes back as an error status instead — so retry once in USD
            // rather than leaving the shopper with an empty picker. The country keeps USD from
            // then on, so this costs one extra call, once.
            //
            // Never when the shopper chose the currency: this fallback is for a default we picked
            // for them, and silently paying in dollars instead of what they asked for is a
            // different transaction, not a recovery. They get told, and the choice stands.
            if (!quotes.length && this._fiatCode !== "USD" && !this._currencyPinned) {
                const usdData = await this._quoteInUsdInstead(amt, cc)
                if (usdData) {
                    data   = usdData
                    quotes = usdData.quotes
                }
            }

            // No providers can fill this amount (too low / too high for every provider in
            // this country) — this is now the real min/max signal, since we no longer
            // pre-block on Meld's unreliable per-currency limits.
            if (!quotes.length) {
                this._showEmpty()
                return
            }
            this._hideEmptyNotice()
            this._platformFee = data.platformFee || 0

            this._allQuotes = this._sortQuotes(quotes)
            this._updateFxRate(this._allQuotes[0])

            // Derive unique payment methods preserving sort order
            const seen = new Set()
            this._paymentMethods = this._allQuotes
                .map(q => q.paymentMethodType)
                .filter(m => m && !seen.has(m) && seen.add(m))

            // Keep the method the user chose whenever it is still on offer. When it isn't, the
            // row has to follow the quotes actually being shown — labelling card quotes as Bank
            // Transfer would be worse than relabelling. The server says when it dropped the
            // filter, so the switch is traceable instead of looking like the picker forgot.
            if (!this._selectedMethod || !this._paymentMethods.includes(this._selectedMethod)) {
                if (this._selectedMethod) {
                    console.info("meld: no quotes for", this._selectedMethod,
                        data.paymentMethodFallback ? "(server dropped the method filter)" : "",
                        "→ showing", this._paymentMethods[0])
                }
                this._selectedMethod = this._paymentMethods[0] || null
            }

            this._applyMethodFilter()

        } catch (e) {
            // Meld rejected the amount and named the real floor for this currency. Learn it, move
            // the field up to it and quote once more, so the shopper lands on a valid amount
            // instead of an empty "You receive". One retry only — a second failure is reported.
            // A rejection that names a provider ("[SWAPPED] Invalid fiat currency provided") is
            // about the currency, not the amount: this route's partners cannot price it at all.
            // Move to dollars rather than reporting an error the shopper can do nothing about.
            if (!isRetry && this._fiatCode !== "USD" && !this._currencyPinned && this._isCurrencyRejection(e)) {
                const usdData = await this._quoteInUsdInstead(amt, cc)
                if (usdData) {
                    await this.fetchQuotes(true)
                    return
                }
            }

            // Only a floor above what we just asked for says anything about this request. A lower
            // one is a number picked out of some other sentence in the message, and acting on it
            // would move the field down for no reason — and re-send a quote that just failed.
            const learned = this._learnMinimum(e)
            if (learned > amt && !isRetry) {
                // The floor Meld named is now folded into the currency's minimum, so the field
                // moves to the usual default for it — a round figure above that floor, never the
                // floor itself. Setting the bare minimum here is what dropped a settled 200 back
                // down to 106 after the first quote.
                this._applyLimitsForCurrency()
                this.amountInputTarget.value = this._formatAmount(this._defaultAmount())
                await this.fetchQuotes(true)
                return
            }
            console.error("meld: quote failed", e)
            this._showEmpty()
        }
    }

    // _learnMinimum reads the floor out of a rejected quote and remembers it for the currency.
    // Returns the minimum, or 0 when the failure was something else. Meld reports it in the
    // currency we asked in, so the number is directly usable as the field's new value.
    _learnMinimum(err) {
        const payload = (err && err.payload) || {}
        const code    = String(payload.code || "")
        const message = String(payload.message || payload.error || "")
        if (code !== "INVALID_AMOUNT_TOO_LOW" && !/too\s*low|minimum/i.test(message)) return 0

        const byCurrency = new RegExp(`([0-9]+(?:[.,][0-9]+)?)\\s*${this._fiatCode}`, "i").exec(message)
        const anyNumber  = byCurrency || /([0-9]+(?:[.,][0-9]+)?)/.exec(message)
        if (!anyNumber) return 0

        const min = Number.parseFloat(String(anyNumber[1]).replace(",", ".")) || 0
        if (min <= 0) return 0
        this._minByCurrency.set(this._fiatCode, min)
        return min
    }

    // Keeps an amount inside the currency's limits. The product floor is already folded into
    // _minAmount by _applyLimitsForCurrency.
    _clampToLimits(amt) {
        if (!(amt > 0)) return 0
        const min = this._minAmount || 0
        const max = this._maxAmount || Infinity
        return Math.min(Math.max(amt, min), max)
    }

    // Re-quotes the same amount in dollars and, if that works, switches the picker over: labels,
    // limits and chips all follow the new currency. Returns the quote payload, or null when
    // dollars are no better — the caller then reports the original failure.
    async _quoteInUsdInstead(amt, cc) {
        let usdData
        try {
            usdData = await this._requestQuotes("USD", amt, cc)
        } catch (e) {
            console.error("meld: USD fallback failed", e)
            return null
        }
        if (!(usdData.quotes || []).length) return null

        // Remembered without limits: the ones on record belong to the currency that just failed,
        // and the dollar route's own bounds are unknown here. The country's currency list survives
        // the switch — the one that failed is still on offer, since it failed for this amount and
        // not necessarily for every one.
        const key        = String(cc).toUpperCase()
        const previous   = this._currencyByCountry.get(key)
        const currencies = (previous && previous.currencies) || []
        // Dollars just quoted successfully, so they belong in the list whatever the coverage feed
        // said — the chip must never show a currency its own dropdown denies.
        this._currencies = currencies.includes("USD") ? currencies : ["USD", ...currencies]
        this._currencyByCountry.set(key, { code: "USD", limits: "[]", currencies: this._currencies })
        this._fiatCode        = "USD"
        this._fxRate          = 0
        this.limitsValue      = "[]"
        this._presetsRendered = false
        this._renderedPresets = []
        this._applyCurrencyLabels("USD")
        this._renderCurrencyChip()
        this._applyLimitsForCurrency()
        // _applyLimitsForCurrency re-seeds the field; the shopper's own amount wins.
        this.amountInputTarget.value = this._formatAmount(amt)
        return usdData
    }

    // True when Meld refused the pair itself rather than the amount — a named provider that
    // cannot price the fiat, or no valid combination at all.
    _isCurrencyRejection(err) {
        const payload = (err && err.payload) || {}
        const code    = String(payload.code || payload.error || "")
        const message = String(payload.message || err && err.message || "")
        if (code === "INCOMPATIBLE_REQUEST" || code === "NO_VALID_QUOTES") return true
        return /invalid fiat currency|no valid quote/i.test(message)
    }

    _requestQuotes(currency, amt, cc) {
        return this._apiFetch("POST", "/meld/api/quote", {
            sourceCurrencyCode:      currency,
            destinationCurrencyCode: this.destCcValue,
            countryCode:             cc,
            sourceAmount:            String(amt),
            paymentMethodType:       this._selectedMethod,
            platformFeeFixed:        0,
            platformFeePercent:      0,
        })
    }

    _applyMethodFilter() {
        // The method the user came back from Compare with is restored before the filter runs, not
        // after. Filtering first and restoring second left the two out of step: the row said Bank
        // Transfer while the list underneath had already been built for Credit / Debit Card.
        const savedProvider = this.selectedProviderValue
        // From the value the server rendered, not from window.location. Compare's Continue
        // submits into the "content" Turbo frame, so the address bar still holds the URL the
        // shopper arrived on — reading the method there returned the previous one, or nothing.
        // The filter then stayed on the default card rail, the provider they had just chosen was
        // filtered out of the list, and the picker fell back to the top quote: choosing a
        // provider on Compare appeared to do nothing.
        const savedMethod   = (this.paymentMethodValue || "").trim()
        if (savedProvider && savedMethod && this._allQuotes.some(q => q.paymentMethodType === savedMethod)) {
            this._selectedMethod = savedMethod
        }

        this._quotes = this._selectedMethod
            ? this._allQuotes.filter(q => q.paymentMethodType === this._selectedMethod)
            : this._allQuotes

        if (!this._quotes.length) { this._showEmpty(); return }

        if (savedProvider) {
            const idx = this._quotes.findIndex(q =>
                q.serviceProvider === savedProvider &&
                (!savedMethod || q.paymentMethodType === savedMethod)
            )
            this._selectedIdx    = idx >= 0 ? idx : 0
            // Choosing on Compare is an explicit pick, so it survives later re-quotes.
            this._selectedProvider = idx >= 0 ? savedProvider : null
            this.selectedProviderValue = ""
        } else if (this._selectedProvider) {
            // Keep the provider the user picked across re-quotes (amount edits, the ~30s
            // countdown refresh, method changes) instead of snapping back to the best one.
            // If their pick is no longer offered we move them to the best quote and drop the
            // pick, rather than silently restoring it if that provider reappears later.
            const idx = this._quotes.findIndex(q => q.serviceProvider === this._selectedProvider)
            this._selectedIdx = idx >= 0 ? idx : 0
            if (idx < 0) this._selectedProvider = null
        } else {
            // Nobody picked anything: always follow the best quote for the current amount.
            // _quotes is sorted best-first, so this re-ranks on every amount edit and refresh.
            this._selectedIdx = 0
        }

        // Compare gets the list the picker is actually working from — filtered by the payment
        // method — not every quote there is. The two used to disagree: the picker showed the best
        // card quote while Compare ranked every rail, so its top row was a provider on a method
        // the shopper had not chosen, and the row that was highlighted sat further down. The buy
        // flow already stored the filtered list, because express quotes one method; this makes the
        // wallet do the same.
        sessionStorage.setItem("meld_quotes", JSON.stringify(this._quotes))

        this._updateQuoteDisplay()
        this._startCountdown()
        this.continueBtnTarget.disabled = !this._quoteCoversPurchase(this._quotes[this._selectedIdx])
    }

    // _quoteCoversPurchase is the buy-checkout rule (COI-833): a quote is usable only if it
    // delivers at least the purchase total. Always true outside the checkout embed.
    _quoteCoversPurchase(q) {
        const total = this.hasPurchaseTotalValue ? this.purchaseTotalValue : 0
        if (!(total > 0) || !q) return true
        return Number.parseFloat(q.destinationAmount || 0) + 1e-6 >= total
    }

    // _renderPurchaseCheck shows a "you'd receive X / purchase needs Y" line under the receive
    // amount whenever a quote the shopper can act on falls short of the checkout total.
    _renderPurchaseCheck(q) {
        if (!this.hasPurchaseCheckTarget) return
        const total = this.hasPurchaseTotalValue ? this.purchaseTotalValue : 0
        const el = this.purchaseCheckTarget
        if (!(total > 0) || !q) { el.style.display = "none"; return }
        // Only the shortfall needs words; a quote that covers the purchase says nothing extra.
        if (this._quoteCoversPurchase(q)) { el.style.display = "none"; return }
        const dest = Number.parseFloat(q.destinationAmount || 0)
        el.textContent = `You'd receive ${this._money(dest)} — your purchase needs ${this._money(total)}. Increase the amount or pick another provider.`
        el.classList.add("meld-purchase-check-short")
        el.style.display = ""
    }

    _setLoading() {
        this.youReceiveAmountTarget.textContent  = "—"
        if (this.hasPurchaseCheckTarget) this.purchaseCheckTarget.style.display = "none"
        this.providerRowTarget.style.display     = "none"
        this.stalenessBarTarget.style.display    = "none"
        this.continueBtnTarget.disabled          = true
        // The rate belongs to a quote; with no quote on screen it would be a leftover from the
        // previous amount. Limits stay — they belong to the currency, not to the quote.
        if (this.hasRateLabelTarget) this.rateLabelTarget.textContent = ""
    }

    // An empty picker with a currency the shopper chose themselves needs a reason, because the one
    // thing they can do about it — pick another currency — is the thing they just did. Left to the
    // default currency the Min/Max line is explanation enough, so nothing is said.
    _showEmpty() {
        this._setLoading()
        if (!this.hasEmptyNoticeTarget) return
        if (!this._currencyPinned) { this._hideEmptyNotice(); return }
        this.emptyNoticeTarget.textContent   = `No provider is quoting ${this._fiatCode} for this amount. Try a different amount, or switch back to ${this._currencies[0] || "another currency"}.`
        this.emptyNoticeTarget.style.display = ""
    }

    _hideEmptyNotice() {
        if (!this.hasEmptyNoticeTarget) return
        this.emptyNoticeTarget.style.display = "none"
    }

    // ── Quote display ─────────────────────────────────────────

    _updateQuoteDisplay() {
        const q = this._quotes[this._selectedIdx]
        if (!q) return

        sessionStorage.setItem("meld_selected_provider", q.serviceProvider || "")
        sessionStorage.setItem("meld_selected_method",   q.paymentMethodType || "")

        const destAmt = Number.parseFloat(q.destinationAmount || 0)
        this.youReceiveAmountTarget.textContent = destAmt > 0
            ? new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(destAmt)
            : "—"
        this._renderPurchaseCheck(q)

        this._renderRateLabel(q)

        this.providerNameTarget.textContent              = this._formatProvider(q.serviceProvider)
        this.providerBestBadgeTarget.style.display       = this._selectedIdx === 0 ? "" : "none"
        this.seeMoreFormTarget.style.display             = ""
        this.compareMethodInputTarget.value              = this._selectedMethod || ""
        // Hand the current amount, provider and country to Compare so the return trip restores all
        // three. The country especially: picking a provider there navigates back to the picker,
        // and a stale country would silently move the shopper to another market.
        this.compareAmountInputTarget.value              = String(this._lastQuotedAmount || "")
        this.compareProviderInputTarget.value            = q.serviceProvider || ""
        if (this.hasCompareCountryInputTarget) {
            this.compareCountryInputTarget.value = this.countryCodeTarget.value || ""
        }
        // Only a currency the shopper chose travels: sending the default back would pin it on the
        // return trip and freeze a choice they never made.
        if (this.hasCompareCurrencyInputTarget) {
            this.compareCurrencyInputTarget.value = this._currencyPinned ? this._fiatCode : ""
        }
        this.providerRowTarget.style.display             = ""

        this._renderProviderLogo(q.serviceProvider)
    }

    _renderProviderLogo(name) {
        const wrapper = this.providerLogoWrapperTarget
        wrapper.replaceChildren()
        const svg = _MELD_PROVIDER_ICONS[name]
        if (svg) {
            wrapper.innerHTML = svg
        } else {
            const badge = document.createElement("span")
            badge.className = "inline-flex items-center justify-center self-center w-4 h-4 rounded-full text-[9px] font-bold flex-shrink-0 dark:bg-dark-primary-color wdark:bg-wdark-primary-color dark:text-dark-button-text-and-icon wdark:text-wdark-button-text-and-icon"
            badge.textContent = (name || "?")[0].toUpperCase()
            wrapper.appendChild(badge)
        }
    }

    _formatProvider(name) {
        if (!name) return "—"
        const known = { MOONPAY: "MoonPay", TOPPER: "Topper", TRANSAK: "Transak", BANXA: "Banxa", SARDINE: "Sardine", WERT: "Wert", STRIPE: "Stripe" }
        return known[name] || name.charAt(0) + name.slice(1).toLowerCase()
    }


    // ── Provider picker (bottom sheet) ────────────────────────

    toggleProviderDropdown() {
        const dd     = this.providerDropdownTarget
        const isOpen = dd.style.display !== "none"
        if (isOpen) {
            dd.style.display = "none"
        } else {
            this._renderProviderList()
            dd.style.display = ""
        }
    }

    _renderProviderList() {
        const list = this.providerListTarget
        list.replaceChildren()
        this._quotes.forEach((q, i) => {
            const item = document.createElement("div")
            item.className = "meld-provider-item" + (i === this._selectedIdx ? " meld-provider-item-active" : "")

            const nameEl       = document.createElement("span")
            nameEl.className   = "font-semibold text-sm"
            nameEl.textContent = this._formatProvider(q.serviceProvider)

            const priceEl       = document.createElement("span")
            priceEl.className   = "text-xs opacity-60"
            priceEl.textContent = Number.parseFloat(q.destinationAmount || 0).toFixed(6).replace(/\.?0+$/, "") + " " + (q.destinationCurrencyCode || "").split("_")[0]

            item.appendChild(nameEl)
            item.appendChild(priceEl)
            item.addEventListener("click", () => {
                this._selectedIdx = i
                this._selectedProvider = q.serviceProvider || null
                this._updateQuoteDisplay()
                this.providerDropdownTarget.style.display = "none"
            })
            list.appendChild(item)
        })
    }

    // ── Payment method ────────────────────────────────────────

    async openPaymentPicker() {
        try {
            const cc = this.countryCodeTarget.value
            let qs = cc ? `&countryCode=${encodeURIComponent(cc)}` : ""
            // Carry the amount so switching method keeps it. Each option in the sheet navigates to
            // /meld afresh, so anything not in that URL is lost.
            if (this._lastQuotedAmount > 0) qs += `&amount=${encodeURIComponent(this._lastQuotedAmount)}`
            const headers = {
                "Turbo-Frame":    "content",
                "Accept":         "text/vnd.turbo-stream.html, text/html",
                "X-CSRF-Token":   this.csrfValue,
            }
            if (this.embedTokenValue) headers["X-Session"] = this.embedTokenValue
            const resp = await fetch(`/deposit/options?meldPaymentPicker=1${qs}`, { headers })
            if (!resp.ok) return
            const html = await resp.text()
            await Turbo.renderStreamMessage(html)
        } catch (e) {
            console.error("meld openPaymentPicker error", e)
        }
    }

    // ── Countdown ─────────────────────────────────────────────

    _startCountdown() {
        this._countdownSecs = 60
        this.stalenessBarTarget.style.display = ""
        this._updateCountdown()
        if (this._countdownTimer) clearInterval(this._countdownTimer)
        this._countdownTimer = setInterval(() => {
            this._countdownSecs--
            if (this._countdownSecs <= 0) {
                clearInterval(this._countdownTimer)
                this._countdownTimer = null
                this.fetchQuotes()
            } else {
                this._updateCountdown()
            }
        }, 1000)
    }

    _updateCountdown() {
        // Pattern comes from the template so it can be translated; {s} is the
        // seconds placeholder. Falls back to English only if the value is absent.
        const pattern = this.countdownLabelValue || "Quote updates in {s}s"
        this.countdownLabelTarget.textContent = pattern.replace("{s}", this._countdownSecs)
    }

    // ── Continue (session creation) ───────────────────────────

    async onContinue() {
        const q = this._quotes[this._selectedIdx]
        if (!q) return

        this.continueBtnTarget.disabled    = true
        this.continueBtnTarget.textContent = this.openingLabelValue || "Opening…"

        let navigated = false
        try {
            const resp = await this._apiFetch("POST", "/meld/api/session", {
                countryCode:             q.countryCode,
                sourceCurrencyCode:      q.sourceCurrencyCode,
                sourceAmount:            String(q.sourceAmount),
                destinationCurrencyCode: q.destinationCurrencyCode,
                serviceProvider:         q.serviceProvider,
                paymentMethodType:       q.paymentMethodType,
                // Informational quote snapshot: the buy-checkout session keeps it so settlement
                // can record quoted-vs-realized figures for this onramp.
                destinationAmount:       String(q.destinationAmount ?? ""),
                totalFee:                String(q.totalFee ?? ""),
            })

            // Prefer serviceProviderWidgetUrl (the provider's own URL) per Meld's guidance:
            // it's opened in a new tab, sidestepping the CSP frame-ancestors blocks that
            // break iframing (Mercuryo) and the Apple Pay / camera-KYC issues iframes cause.
            // Fall back to widgetUrl if a provider doesn't return the direct URL.
            const providerUrl = resp.serviceProviderWidgetUrl || resp.widgetUrl
            // A session with no URL is a provider that cannot take this combination. Nothing to
            // open, so say so here instead of leaving the button to spring back with no reason.
            if (!providerUrl) {
                await this._showUnavailableSheet()
                return
            }

            sessionStorage.setItem("meld_widget_url", providerUrl)

            if (meldOpensProviderDirectly({ skipConfirm: this.skipConfirmValue, express: this._express, payIn: this._expressPayIn() })) {
                navigated = true
                this._sessionStarted = true
                meldLaunchProvider(providerUrl)
                return
            }

            const fmt2 = (n) => (parseFloat(n) || 0).toFixed(2)
            const form = this.confirmFormTarget
            const set  = (n, v) => { const el = form.elements.namedItem(n); if (el) el.value = v }
            set("amount",          fmt2(q.sourceAmount || this._parseAmount(this.amountInputTarget.value)))
            set("destAmount",      fmt2(q.destinationAmount))
            set("currency",        q.sourceCurrencyCode || this._fiatCode)
            set("destCurrency",    (q.destinationCurrencyCode || "").split("_")[0])
            set("provider",        q.serviceProvider   || "")
            set("paymentMethod",   q.paymentMethodType || "")
            set("exchangeRate",    String(parseFloat(q.exchangeRate) || 0))
            set("networkFee",      fmt2(q.networkFee      ?? q.networkFeeUsd ?? 0))
            set("transactionFee",  fmt2(q.transactionFee  ?? 0))
            set("partnerFee",      fmt2(q.partnerFee      ?? 0))
            set("totalFee",        fmt2(q.totalFee        ?? 0))
            set("platformFee",     fmt2(this._platformFee || 0))
            set("lowKyc",          q.lowKyc ? "1" : "0")
            set("countryCode",     this.countryCodeTarget.value || "")

            navigated = true
            this._sessionStarted = true
            form.requestSubmit()

        } catch (e) {
            if (e?.payload?.error === "payment_in_progress") {
                // A payment is already pending for this checkout (COI-1305). Keep Continue
                // disabled and let the checkout swap to its payment-in-progress state.
                navigated = true
                if (globalThis.parent !== globalThis.self && this.checkoutOriginValue) {
                    globalThis.parent.postMessage({ type: CHECKOUT_ONRAMP_PAYMENT_IN_PROGRESS }, this.checkoutOriginValue)
                }
            } else if (e?.payload?.error === "amount_below_purchase") {
                // Server re-quoted this provider at the submitted amount and it no longer covers
                // the purchase (COI-833). Raise the amount to what would and re-quote so the
                // shopper sees the corrected deal instead of a dead Continue.
                const d        = e.payload
                const required = Number.parseFloat(d.requiredSourceAmount) || 0
                this._showAmountError(
                    `${this._formatProvider(q.serviceProvider)} would deliver ${this._money(Number.parseFloat(d.destinationAmount) || 0)} ` +
                    `at this amount — your purchase needs ${this._money(Number.parseFloat(d.purchaseTotal) || 0)}.` +
                    (required > 0 ? ` Amount raised to ${this._money(required)}.` : "")
                )
                if (required > 0) {
                    this.amountInputTarget.value = this._formatAmount(required)
                    this._selectedProvider = q.serviceProvider
                    this.fetchQuotes()
                }
            } else {
                console.error("meld session error", e)
                // Meld refusing to open a session for this provider is the last thing we can learn
                // before the shopper leaves the app — after this point the provider's own page is the
                // one saying no, with no way back into the picker. Express mode is the exception: it
                // has its own fallback to the manual picker, which is more useful than a dead end.
                if (!this._express) await this._showUnavailableSheet()
            }
        }

        if (!navigated) {
            this.continueBtnTarget.disabled    = false
            // Restore what the server rendered; hardcoding "Continue" here would
            // overwrite the translated label with English.
            this.continueBtnTarget.textContent = this._continueLabel
        }
    }

    // Renders the "onramp currently unavailable" sheet. Same fetch-and-render path the channel
    // service uses for the other deposit sheets, plus the embed token, since in the buy iframe the
    // session cookie is withheld and the route would answer 401.
    async _showUnavailableSheet() {
        try {
            const headers = { "X-Requested-With": "XMLHttpRequest", "Accept": "text/vnd.turbo-stream.html" }
            if (this.embedTokenValue) headers["X-Session"] = this.embedTokenValue
            const resp = await fetch("/meld/deposit-unavailable", { headers, credentials: "same-origin" })
            if (!resp.ok) return
            Turbo.renderStreamMessage(await resp.text())
        } catch (e) {
            console.error("meld: failed to open unavailable sheet", e)
        }
    }

    // ── Helpers ───────────────────────────────────────────────

    _sortQuotes(quotes) {
        return [...quotes].sort((a, b) => {
            const vDiff = meldQuoteValue(b) - meldQuoteValue(a)
            if (vDiff !== 0) return vDiff
            const aLow = a.lowKyc ? 1 : 0
            const bLow = b.lowKyc ? 1 : 0
            if (bLow !== aLow) return bLow - aLow
            const rateDiff = (parseFloat(b.exchangeRate) || 0) - (parseFloat(a.exchangeRate) || 0)
            if (rateDiff !== 0) return rateDiff
            return (b.customerScore || 0) - (a.customerScore || 0)
        })
    }

    // ── Buy-checkout express ──────────────────────────────────

    // _expressQuoteAndAdvance runs a single reverse quote (destinationAmount = checkout total),
    // picks the best provider (best receive first, same order as the picker), and submits straight
    // to Confirm & Pay. Any failure
    // — network error, no providers — reveals the normal amount→provider picker so the shopper
    // can still proceed manually (the "show the payment-options step" fallback).
    async _expressQuoteAndAdvance() {
        const cc = this.countryCodeTarget.value
        if (!cc) { this._exitExpress(); return }
        // Onramp-only checkout: the shopper has already been shown the all-in total, so quote every
        // provider at exactly that pay-in instead of sizing each one to the receive target.
        const payIn = this._expressPayIn()
        const amount = payIn > 0
            ? { sourceAmount: payIn.toFixed(2) }
            : { destinationAmount: String(this.expressAmountValue) }
        try {
            const data = await this._apiFetch("POST", "/meld/api/quote", {
                sourceCurrencyCode:      this._fiatCode,
                destinationCurrencyCode: this.destCcValue,
                countryCode:             cc,
                ...amount,
                // Honour the chosen method. Without it the express quote is unfiltered, the top
                // quote can be any rail, and Confirm — plus its back link — reports whatever won,
                // which is how a picked Apple Pay came back as Credit / Debit Card.
                paymentMethodType:       this._selectedMethod,
                platformFeeFixed:        0,
                platformFeePercent:      0,
            })

            const quotes = data.quotes || []
            if (!quotes.length) { this._exitExpress(); return }

            this._platformFee      = data.platformFee || 0
            // Same ranking as the picker and Compare — best receive first, low-KYC only as a
            // tiebreaker. Express used to rank low-KYC first, so the provider it auto-advanced to
            // could sit third in "see more quotes", which reads as the wrong provider being chosen.
            this._allQuotes        = this._sortQuotes(quotes)
            this._quotes           = this._allQuotes
            // Express auto-picks the top quote; that is our choice, not the shopper's, so it stays
            // unpinned — if express falls back to the picker, re-quotes still follow the best rate.
            this._selectedIdx      = 0
            sessionStorage.setItem("meld_quotes", JSON.stringify(this._allQuotes))

            // Reuse the normal session-create + confirm submit. It navigates the #content frame
            // to Confirm & Pay on success; if it didn't (session error), fall back to the picker.
            await this.onContinue()
            if (!this._sessionStarted) this._exitExpress()
        } catch (e) {
            console.error("meld express quote failed", e)
            this._exitExpress()
        }
    }

    // _expressPayIn is the all-in pay-in to quote at, or 0 for receive-target sizing. The all-in
    // total is a USD figure, so a shopper paying in another currency keeps receive-target sizing.
    _expressPayIn() {
        if (!this.hasExpressPayAmountValue || !(this.expressPayAmountValue > 0)) return 0
        return this._fiatCode === "USD" ? this.expressPayAmountValue : 0
    }

    // _exitExpress leaves express mode and shows the standard picker as the fallback step,
    // pre-sized to the checkout amount so the manual flow starts from the right number.
    _exitExpress() {
        this._express = false
        this._showExpressLoading(false)
        if (this.expressAmountValue > 0) {
            // expressAmount is the checkout total in the destination token (≈ USD), so in a local
            // currency the field has to start from the equivalent pay-in, not the token figure.
            // Without a rate yet we show it as-is, exactly as before.
            const start = this._fiatCode !== "USD" && this._fxRate > 0
                ? this.expressAmountValue * this._fxRate
                : this.expressAmountValue
            // The picker's "you pay" input must open on at least the sized pay-in (_productMin,
            // a USD figure — convert it the same way) — quoting the bare total sizes a deposit
            // that lands short after fees (COI-833).
            const floor = this._fiatCode !== "USD" && this._fxRate > 0
                ? (this._productMin || 0) * this._fxRate
                : (this._productMin || 0)
            const fallback = Math.max(start, floor)
            this.amountInputTarget.value = this._formatAmount(fallback)
        }
        this.fetchQuotes()
    }

    _showExpressLoading(on) {
        if (!this.hasExpressLoadingTarget) return
        this.expressLoadingTarget.style.display = on ? "" : "none"
    }

    // ── Provider overlay ──────────────────────────────────────

    openProviderOverlay(widgetUrl, directUrl, providerName) {
        // Stay in-app: desktop popup / mobile full-page load, never an iframe.
        meldLaunchProvider(directUrl || widgetUrl)
    }

}
